import ClassroomEditSession from "@/js/components/ClassroomEditSession";
import ClassroomStartSession from "@/js/components/ClassroomStartSession";
import ClassroomTileV2 from "@/js/components/ClassroomTileV2";
import FullPageLoader from "@/js/components/FullPageLoader";
import Nav from "@/js/components/Nav";
import { withRouter } from "@/js/components/Root/withRouter";
import eventService from "@/js/services/eventService";
import { classroomActiveInfoV2 } from "@/js/utils/classroomSessionUtils";
import { getFeatures, useClasswizeConfigStore } from "@/stores/classwizeConfigStore";
import useStore from "@/stores/index";
import { useClassroomSessionStore } from "@/stores/v2/classroomSessionStore";
import { useUserSessionStore } from "@/stores/v2/userSessionStore";
import { CLASSROOM_TYPES } from "@/types/classrooms";
import { Accordion, AccordionButton, AccordionIcon, AccordionItem, AccordionPanel, Box, Heading } from "@chakra-ui/react";
import { SkipNavContent } from "@chakra-ui/skip-nav";
import _ from "lodash";
import PropTypes from "prop-types";
import React from "react";
import { withTranslation } from "react-i18next";
import shallow from "zustand/shallow";
import { CtxBarRight } from "./CtxBarRight";
import { TeacherGoogleSyncModal } from "./TeacherGoogleSyncModal";

class Classrooms extends React.Component {
	constructor(props) {
		const store = useStore.getState();
		super(props);
		this.state = {
			/**
			 * Only show the "We're loading your classes as fast as we can." message if the initial load is not complete.
			 * This is to prevent the message from flashing when the classrooms are refetched upon receiving a CLASS_STARTED event.
			 */
			initialLoadComplete: false,
			features: getFeatures(),

			isMonitoringAuthorised: !!_.intersection(
				useClasswizeConfigStore.getState().data.monitor_groups || [],
				useUserSessionStore.getState().getUsersGroups()
			).length,

			showStartClassModal: false,
			showEditClassModal: false,
			showMonitoringClassActiveWarning: false,
			openGoogleSyncModal: false,
			currentMonitoring: {},
			startClassSessionError: false,
			startClassSessionIsMonitoring: false,
			selectedClassroomName: null,
			classExpiryStr: null,
			showUnpinnedClasses: store.classroom.showUnpinnedClasses,
			teacherClassroomsSyncEnabled: store.classroom.teacherClassroomsSyncEnabled,
			featureToggles: useStore.getState().featureToggles,
		};
		this.unsubClasswizeConfig = () => {};
		this.unsubChatWindowOpenStatus = () => {};
		this.unsubChatWindowMinimized = () => {};
		this.unsubClassroomStore = () => {};
		this.unsubShowUnpinnedClasses = () => {};
		this.unsubTeacherClassroomsSyncEnabled = () => {};
		this.unsubClassroomSessionStore = () => {};

		this.navRef = React.createRef();

		this.unsubUserSessionStore = () => {};
		this.unsubEventServiceStatus = () => {};
	}

	handleClassStartedEvent = () => {
		useStore.getState().classroom.fetchClassrooms();
	};

	addEventServiceListeners = () => {
		if (eventService.eventSource) {
			eventService.eventSource.addEventListener("CLASS_STARTED", this.handleClassStartedEvent);
		}
	};

	removeEventServiceListeners = () => {
		if (eventService.eventSource) {
			eventService.eventSource.removeEventListener("CLASS_STARTED", this.handleClassStartedEvent);
		}
	};

	componentDidMount() {
		useStore.getState().classroom.fetchClassrooms();

		this.unsubUserSessionStore = useUserSessionStore.subscribe(this.onUserSessionChanged);

		this.unsubClassroomSessionStore = useClassroomSessionStore.subscribe(
			(state) => state,
			(state) => {
				this.onChangeClassroomSessionStore(state);
			}
		);

		this.unsubClassroomStore = useStore.subscribe(
			(state) => [state.classroom.loaded, state.classroom.dataV2],
			() => {
				if (this.state.initialLoadComplete) {
					return;
				}
				this.setState({
					initialLoadComplete: useStore.getState().classroom.loaded && !useStore.getState().classroom.fetchInProgress,
				});
			}
		);

		this.unsubShowUnpinnedClasses = useStore.subscribe(
			(state) => [state.classroom.showUnpinnedClasses],

			() =>
				this.setState({
					showUnpinnedClasses: useStore.getState().classroom.showUnpinnedClasses,
				}),
			shallow
		);

		this.unsubTeacherClassroomsSyncEnabled = useStore.subscribe(
			(state) => [state.classroom.teacherClassroomsSyncEnabled],
			() =>
				this.setState({
					teacherClassroomsSyncEnabled: useStore.getState().classroom.teacherClassroomsSyncEnabled,
				}),
			shallow
		);

		this.unsubChatWindowOpenStatus = useStore.subscribe(
			(state) => [state.chat.chatWindowOpen],

			() =>
				this.setState({
					isChatWindowOpen: useStore.getState().chat.chatWindowOpen,
				}),
			shallow
		);

		this.unsubChatWindowMinimized = useStore.subscribe(
			(state) => [state.chat.chatWindowMinimized],

			() =>
				this.setState({
					isChatWindowMinimized: useStore.getState().chat.chatWindowMinimized,
				})
		);

		setTimeout(() => {
			useClassroomSessionStore.getState().fetchAndPopulateMaxEndTime();
		}, 0);

		this.addEventServiceListeners();

		// Subscribe to event service status changes to re-add listeners when service becomes available
		this.unsubEventServiceStatus = useStore.subscribe(
			(state) => state.eventServiceStatus.status,
			() => {
				this.addEventServiceListeners();
			}
		);
	}

	componentWillUnmount() {
		this.unsubUserSessionStore();

		this.unsubClasswizeConfig();
		this.unsubChatWindowOpenStatus();
		this.unsubChatWindowMinimized();
		this.unsubClassroomStore();
		this.unsubClassroomSessionStore();
		this.unsubTeacherClassroomsSyncEnabled();

		this.removeEventServiceListeners();
		this.unsubEventServiceStatus();
	}

	onChangeClassroomSessionStore = (state) => {
		this.setState({
			showStartClassModal: state.showStartClassModal,
			showEditClassModal: state.showEditClassModal,
			startClassSessionError: state.startClassSessionError,
			startClassSessionIsMonitoring: state.selectedClassroomMonitoring,
			selectedClassroomName: state.selectedClassroomName,
			classExpiryStr: state.classExpiryStr,
			showMonitoringClassActiveWarning: state.showMonitoringClassActiveWarning,
			currentMonitoring: state.currentMonitoring,
		});
	};

	onUserSessionChanged = () => {
		this.setState({
			isMonitoringAuthorised: !!_.intersection(
				useClasswizeConfigStore.getState().data.monitor_groups || [],
				useUserSessionStore.getState().getUsersGroups()
			).length,
		});
	};

	handleAction = (action, classroom) => {
		switch (action) {
			case TEACHER_ACTIONS.save:
				this.handleSubmitAddClassroom(classroom.sourceId);
				break;
			case TEACHER_ACTIONS.delete:
				this.handleSubmitDeleteClassroom(classroom);
				break;
			case TEACHER_ACTIONS.open:
				useStore.getState().classroom.setClassDetailsLoading();
				useUserSessionStore.getState().setActiveClassroom(classroom.sourceId);
				// eslint-disable-next-line no-case-declarations
				const classroomV2 = useStore.getState().classroom.getClassroomV2(classroom.id);
				if (this.coursesLicensed && !this.classroomLicensed) {
					this.props.router.navigate(`/classroom/${classroomV2.id}/courses`);
				} else {
					this.props.router.navigate(`/classroom/${classroomV2.id}`);
				}
				break;
			case TEACHER_ACTIONS.cancel:
				this.handleCancel();
				break;
			default:
				break;
		}
	};

	mapClassroomsToComponent = (classrooms) =>
		classrooms.map((c) => {
			const [isActive, expiry] = classroomActiveInfoV2(c);
			return <ClassroomTileV2 key={c.id} classroom={c} isActive={isActive} expiry={expiry} onClick={this.handleAction} />;
		});

	sortClassroomTiles = (previousClass, nextClass) => {
		const _previousClassName = `${
			previousClass.type.toLowerCase() === CLASSROOM_TYPES.GROUP ? previousClass.description : previousClass.name
		}${previousClass.sourceId}`;
		const _nextClassName = `${nextClass.type.toLowerCase() === CLASSROOM_TYPES.GROUP ? nextClass.description : nextClass.name}${
			nextClass.sourceId
		}`;
		return (previousClass?.personalisation?.displayName || _previousClassName).localeCompare(
			nextClass?.personalisation?.displayName || _nextClassName
		);
	};

	availableClassrooms = (showUnpinnedClasses) => {
		// sort classrooms by name in ascending order
		const pinnedClassrooms = useStore.getState().classroom.getPinnedClassrooms();
		const unpinnedClassrooms = useStore.getState().classroom.getUnpinnedClassrooms();

		if (pinnedClassrooms && pinnedClassrooms.length > 0) {
			const pinnedClassroomsSorted = pinnedClassrooms.sort(this.sortClassroomTiles);
			const unpinnedClassroomsSorted = unpinnedClassrooms.sort(this.sortClassroomTiles);
			return (
				<main>
					<SkipNavContent />
					<section>
						<Heading as="h2" fontSize={14} fontWeight={500} fontFamily="Roboto" p={[7, 7, 7, 7]}>
							{this.props.t("overview.pinnedClasses")}
						</Heading>
						<Box className="Classrooms_Pinned_Container" aria-label={this.props.t("overview.pinnedClasses")}>
							{this.mapClassroomsToComponent(pinnedClassroomsSorted)}
						</Box>
					</section>
					<section>
						<Box mx={8} my={4}>
							<Accordion defaultIndex={[0]} allowMultiple aria-label={this.props.t("overview.otherClasses")}>
								<AccordionItem border={"1px solid #fefefe"} rounded={"md"}>
									<AccordionButton
										background={"white"}
										_hover={{
											background: "#fafafa",
										}}
										px={5}
										py={3}
										roundedTop={"md"}
									>
										<Box color={"#333"} mr={"auto"}>
											{this.props.t("overview.otherClasses")}
										</Box>
										<AccordionIcon
											__css={{
												boxSize: "24px !important",
											}}
											color={"#333"}
										/>
									</AccordionButton>
									<AccordionPanel pb={4}>
										{showUnpinnedClasses && (
											<Box className="Classrooms_Unpinned_Container">{this.mapClassroomsToComponent(unpinnedClassroomsSorted)}</Box>
										)}
									</AccordionPanel>
								</AccordionItem>
							</Accordion>
						</Box>
					</section>
				</main>
			);
		}

		const classroomsSorted = useStore.getState().classroom.getClassrooms().sort(this.sortClassroomTiles);
		return <div className="Classrooms_container">{this.mapClassroomsToComponent(classroomsSorted)}</div>;
	};

	handleSubmitAddClassroom = async (classroomName, isMonitoringClass, isPinned) => {
		return await useStore.getState().classroom.handleAddClassroom({
			name: classroomName,
			isMonitoringClass,
			pinned: isPinned,
		});
	};

	handleSubmitDeleteClassroom = (classroom) => {
		useStore.getState().classroom.handleDeleteClassroom(classroom.stableIdV1);
	};

	handleCancel = () => {};

	handleEditClassroomModalClose = () => {
		this.setState({
			showEditClassModal: false,
		});
	};

	handleOpenGoogleSyncModal = () => {
		this.setState({ openGoogleSyncModal: true });
	};

	handleCloseGoogleSyncModal = () => {
		this.setState({ openGoogleSyncModal: false });
	};

	render() {
		if (this.state.initialLoadComplete) {
			return (
				<div>
					<Nav />
					<div className="ctxbar ctxbar--class-selection">
						{this.state.openGoogleSyncModal && this.state.teacherClassroomsSyncEnabled && (
							<TeacherGoogleSyncModal close={this.handleCloseGoogleSyncModal} />
						)}
						<CtxBarRight
							teacherClassroomsEnabled={useStore.getState().classroom.teacherClassroomsEnabled}
							isMonitoringAuthorised={this.state.isMonitoringAuthorised}
							teacherClassroomsSyncEnabled={this.state.teacherClassroomsSyncEnabled}
							onSyncClick={this.handleOpenGoogleSyncModal}
						/>
					</div>
					{this.availableClassrooms(this.state.showUnpinnedClasses)}
					<ClassroomStartSession
						open={this.state.showStartClassModal}
						onStartError={this.state.startClassSessionError}
						isMonitoringClass={this.state.startClassSessionIsMonitoring}
					/>
					<ClassroomEditSession
						open={this.state.showEditClassModal}
						onExtendError={this.state.startClassSessionError}
						durationPlaceholder={this.state.classExpiryStr}
					/>
				</div>
			);
		} else {
			return <FullPageLoader message={this.props.t("overview.loaderMessage")} />;
		}
	}
}

Classrooms.propTypes = {
	router: PropTypes.object.isRequired,
	t: PropTypes.func.isRequired,
};

export default withTranslation()(withRouter(Classrooms));

export const TEACHER_ACTIONS = {
	save: "save",
	cancel: "cancel",
	delete: "delete",
	open: "open",
};
